import {
  type FormEvent,
  forwardRef,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Icon } from './icons.js';
import { assignRef, NativeSelectPicker, useCompactPicker } from './picker-sheet.js';

export interface SearchInputState {
  /** The validation message of an explicit search (Enter) that is too short. */
  readonly message: string | undefined;
  readonly setText: (text: string) => void;
  /** Applies the typed text now (Enter), or reports why it cannot. */
  readonly submit: () => void;
  readonly text: string;
}

/**
 * The toolbar search: typed text is applied after a short pause once it is empty or acceptable, and
 * at once on Enter; text that cannot be applied is never sent (for example below the contract's
 * 3-character minimum, or not yet a full mobile number). `applied` is the value in effect (from the
 * URL); an outside change, such as «پاک‌کردن فیلترها», replaces the typed text.
 */
export function useSearchInput({
  accept,
  applied,
  delayMs = 500,
  invalidMessage,
  minLength = 0,
  onApply,
}: {
  /** Whether non-empty text can be applied; defaults to «at least `minLength` characters». */
  readonly accept?: (text: string) => boolean;
  readonly applied: string;
  readonly delayMs?: number;
  /** Shown when Enter is pressed on text that cannot be applied. */
  readonly invalidMessage?: string;
  readonly minLength?: number;
  readonly onApply: (text: string) => void;
}): SearchInputState {
  const [text, setTextState] = useState(applied);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const lastApplied = useRef(applied);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  useEffect(() => {
    if (applied !== lastApplied.current) {
      lastApplied.current = applied;
      setTextState(applied);
      setMessage(undefined);
    }
  }, [applied]);

  const rejected = (value: string) => {
    if (value === '') {
      return false;
    }
    const check = acceptRef.current;
    return check === undefined ? [...value].length < minLength : !check(value);
  };

  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed === lastApplied.current || rejected(trimmed)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      lastApplied.current = trimmed;
      onApplyRef.current(trimmed);
    }, delayMs);
    return () => clearTimeout(timer);
    // `rejected` reads `minLength` and the latest `accept` through a ref.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [delayMs, text, minLength]);

  return {
    message,
    setText: (value) => {
      setTextState(value);
      setMessage(undefined);
    },
    submit: () => {
      const trimmed = text.trim();
      if (rejected(trimmed)) {
        setMessage(invalidMessage);
        return;
      }
      setMessage(undefined);
      if (trimmed !== lastApplied.current) {
        lastApplied.current = trimmed;
        onApplyRef.current(trimmed);
      }
    },
    text,
  };
}

/**
 * The compact list toolbar of the prototype (`.ops-toolbar`): a search field with its icon, select
 * chips (`.filter-select`), a «فیلتر بیشتر» button for the remaining filters, and trailing actions such
 * as «خروجی». It is a search landmark; pressing Enter in the search field submits it.
 */
export function FilterToolbar({
  children,
  label,
  message,
  onSubmit,
  trailing,
}: {
  readonly children: ReactNode;
  /** The accessible name of the search landmark (for example «فیلتر فروشگاه‌ها»). */
  readonly label: string;
  /** A validation message for the search (for example a query that is too short). */
  readonly message?: string | undefined;
  readonly onSubmit: () => void;
  readonly trailing?: ReactNode;
}) {
  const messageId = useId();
  return (
    <form
      aria-describedby={message === undefined ? undefined : messageId}
      aria-label={label}
      className="saay-toolbar saay-filter-toolbar"
      noValidate
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSubmit();
      }}
      role="search"
    >
      <div className="saay-filter-toolbar__group">{children}</div>
      {trailing === undefined ? null : (
        <div className="saay-filter-toolbar__trailing">{trailing}</div>
      )}
      {message === undefined ? null : (
        <p className="saay-filter-toolbar__message" id={messageId} role="alert">
          {message}
        </p>
      )}
    </form>
  );
}

export interface SearchFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'aria-label'
> {
  /** The accessible name; the prototype field has no visible label. */
  readonly label: string;
  readonly invalid?: boolean;
}

/** The toolbar search field (prototype `.ops-search`): a search icon inside a compact input. */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { className, invalid = false, label, onKeyDown, ...rest },
  ref,
) {
  return (
    <label className={['saay-search', className].filter(Boolean).join(' ')}>
      <Icon name="search" />
      <input
        {...rest}
        aria-invalid={invalid || undefined}
        aria-label={label}
        enterKeyHint="search"
        onKeyDown={(event) => {
          onKeyDown?.(event);
          // A toolbar with several inputs (a combobox beside the search) has no implicit submission.
          if (event.key === 'Enter' && !event.defaultPrevented && !event.nativeEvent.isComposing) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }
        }}
        ref={ref}
        type="search"
      />
    </label>
  );
});

export interface FilterSelectOption {
  readonly label: string;
  readonly value: string;
}

export interface FilterSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'children' | 'aria-label'
> {
  readonly label: string;
  readonly options: readonly FilterSelectOption[];
  /** The «همه …» option for no filter (value `''`). */
  readonly placeholder: string;
}

/** A select chip of the toolbar (prototype `.filter-select`), applied on change. */
export const FilterSelect = forwardRef<HTMLSelectElement, FilterSelectProps>(function FilterSelect(
  { className, id, label, options, placeholder, ...rest },
  ref,
) {
  // Phones open the option picker (PickerSheet) instead of the platform's select popup.
  const compact = useCompactPicker();
  const generated = useId();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const select = (
    <select
      {...rest}
      aria-label={label}
      {...(compact ? { 'aria-hidden': true, tabIndex: -1 } : {})}
      className={['saay-filter-select', className].filter(Boolean).join(' ')}
      {...(id === undefined ? {} : { id })}
      ref={(node) => {
        selectRef.current = node;
        assignRef(ref, node);
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
  if (!compact) {
    return select;
  }
  return (
    <span className="saay-filter-select-picker" data-picker>
      {select}
      <NativeSelectPicker
        ariaLabel={label}
        disabled={rest.disabled}
        id={`${id ?? generated}-picker`}
        includeEmpty
        label={label}
        selectRef={selectRef}
      />
    </span>
  );
});

export interface SegmentedOption<K extends string> {
  readonly key: K;
  readonly label: string;
}

/**
 * The prototype segmented control (`.segmented-control`) for a single choice among a few presets,
 * for example «همه / امروز / این هفته». It is a radio group: arrow keys move the choice (in RTL the
 * visual next option is to the left).
 */
export function SegmentedControl<K extends string>({
  label,
  onChange,
  options,
  value,
}: {
  readonly label: string;
  readonly onChange: (key: K) => void;
  readonly options: readonly SegmentedOption<K>[];
  /** The selected key; a value outside `options` (for example a custom range) selects none. */
  readonly value: K | undefined;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(
    0,
    options.findIndex((option) => option.key === value),
  );

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, number> = {
      ArrowDown: 1,
      ArrowLeft: 1,
      ArrowRight: -1,
      ArrowUp: -1,
    };
    const move = moves[event.key];
    if (move === undefined || options.length === 0) return;
    event.preventDefault();
    const next = (index + move + options.length) % options.length;
    const option = options[next];
    if (option !== undefined) {
      onChange(option.key);
      refs.current[next]?.focus();
    }
  }

  return (
    <div aria-label={label} className="saay-segmented" onKeyDown={onKeyDown} role="radiogroup">
      {options.map((option, position) => (
        <button
          aria-checked={option.key === value}
          key={option.key}
          onClick={() => onChange(option.key)}
          ref={(element) => {
            refs.current[position] = element;
          }}
          role="radio"
          tabIndex={position === index ? 0 : -1}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
